/**
 * 🗜 **여러 파일을 하나로 묶기(ZIP)** — 저장 전용(압축 안 함).
 *
 * ## 왜 라이브러리를 안 쓰나
 * 묶을 것이 **mp3** 다. 이미 압축된 파일이라 다시 압축해 봐야 1~2% 줄고, 그 대가로 CPU 와
 * 의존성 하나가 는다. ZIP 규격에는 「그냥 담기(store, method 0)」가 있고 그게 딱 맞는다.
 * 담기만 하는 ZIP 은 헤더 세 종류가 전부라 여기서 다 만들 수 있다.
 *
 * ## 🔴 한글 파일 이름
 * 옛 ZIP 은 이름을 그 나라 코드페이지로 적었다. 그래서 **플래그 11번 비트(UTF-8)를 안 켜면**
 * 윈도 탐색기에서 제목이 깨져 보인다. 파일은 멀쩡한데 이름만 깨지니, 받은 사람은 뭐가
 * 잘못됐는지 알 수가 없다. 켜는 걸 잊지 말 것.
 *
 * ## 크기 한계
 * ZIP64 는 구현하지 않았다 — 4GB 넘는 묶음이나 65,535개 넘는 파일은 못 담는다.
 * 읽어주기 한 권은 파일 70개 · 4MB 남짓이라 한참 아래다. 넘으면 **던진다**(조용히 깨진
 * 파일을 주는 것보다 낫다).
 */

/** ZIP 이 쓰는 CRC-32(IEEE 802.3). 표를 한 번만 만들어 두고 쓴다. */
const TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

export interface ZipEntry {
  /** 묶음 안에서의 경로. `폴더/파일.mp3` 처럼 `/` 로 나눈다(역슬래시 금지 — 규격이다). */
  name: string;
  bytes: Uint8Array;
}

/** 파일 65,535개·4GB — ZIP64 없이 담을 수 있는 한계. */
const MAX_FILES = 0xffff;
const MAX_BYTES = 0xffffffff;

/** DOS 형식 시각. 1980 년 이전은 표현할 수 없어 그 해로 눌러 둔다. */
function dosTime(d: Date): { time: number; date: number } {
  const y = Math.max(1980, d.getFullYear());
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1),
    date: ((y - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

/**
 * 담기 전용 ZIP 한 덩어리.
 *
 * @param at 파일에 적을 시각. 안 주면 지금. **테스트에서는 반드시 고정값을 준다** —
 *           안 그러면 같은 입력이 매번 다른 바이트가 되어 비교할 수가 없다.
 */
export function zipStore(entries: readonly ZipEntry[], at: Date = new Date()): Uint8Array {
  if (entries.length > MAX_FILES) {
    throw new Error(`한 묶음에 ${MAX_FILES}개까지만 담을 수 있어요(지금 ${entries.length}개).`);
  }
  const enc = new TextEncoder();
  const { time, date } = dosTime(at);

  const named = entries.map((e) => {
    const name = enc.encode(e.name.split("\\").join("/"));
    if (e.bytes.length > MAX_BYTES) throw new Error(`파일이 너무 커요: ${e.name}`);
    return { name, bytes: e.bytes, crc: crc32(e.bytes) };
  });

  const local = named.reduce((n, f) => n + 30 + f.name.length + f.bytes.length, 0);
  const central = named.reduce((n, f) => n + 46 + f.name.length, 0);
  const total = local + central + 22;
  if (total > MAX_BYTES) throw new Error("묶음이 4GB를 넘어요. 나눠서 받아 주세요.");

  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  let pos = 0;
  const u16 = (v: number) => {
    view.setUint16(pos, v, true);
    pos += 2;
  };
  const u32 = (v: number) => {
    view.setUint32(pos, v >>> 0, true);
    pos += 4;
  };
  const raw = (b: Uint8Array) => {
    out.set(b, pos);
    pos += b.length;
  };

  // ── 파일마다: 지역 헤더 + 내용 ───────────────────────────
  const offsets: number[] = [];
  for (const f of named) {
    offsets.push(pos);
    u32(0x04034b50);
    u16(20); // 풀려면 이 버전이면 된다
    u16(0x0800); // 🔴 UTF-8 이름 — 이 비트를 빼면 한글 이름이 깨진다
    u16(0); // 담기(압축 안 함)
    u16(time);
    u16(date);
    u32(f.crc);
    u32(f.bytes.length); // 담기라 압축 크기 = 원래 크기
    u32(f.bytes.length);
    u16(f.name.length);
    u16(0); // extra 없음
    raw(f.name);
    raw(f.bytes);
  }

  // ── 가운데 목록(central directory) ───────────────────────
  const cdStart = pos;
  for (let i = 0; i < named.length; i++) {
    const f = named[i]!;
    u32(0x02014b50);
    u16(20); // 만든 쪽 버전
    u16(20); // 풀려면 필요한 버전
    u16(0x0800);
    u16(0);
    u16(time);
    u16(date);
    u32(f.crc);
    u32(f.bytes.length);
    u32(f.bytes.length);
    u16(f.name.length);
    u16(0); // extra
    u16(0); // 설명
    u16(0); // 디스크 번호
    u16(0); // 내부 속성
    u32(0); // 외부 속성
    u32(offsets[i]!);
    raw(f.name);
  }

  // ── 끝맺음(EOCD) ────────────────────────────────────────
  /**
   * 🔴 목록 크기를 **먼저 재 둔다.** `u32(pos - cdStart)` 라고 쓰면 그 사이에 `pos` 가
   * 이미 12바이트 나아가 있어서 크기가 12 크게 적힌다. 윈도 탐색기는 지역 헤더를 훑어
   * 그래도 열어 주지만(그래서 손으로 풀어 보면 멀쩡하다), 규격대로 목록을 읽는 프로그램은
   * 「손상된 파일」이라고 한다. 눈으로는 못 잡는 종류다 — 테스트가 잡았다.
   */
  const cdSize = pos - cdStart;
  u32(0x06054b50);
  u16(0);
  u16(0);
  u16(named.length);
  u16(named.length);
  u32(cdSize);
  u32(cdStart);
  u16(0); // 묶음 설명 없음
  return out;
}

/**
 * 🪟 **파일 이름으로 쓸 수 있게 다듬는다.**
 *
 * 윈도가 막는 글자와 제어문자를 뺀다. 끝의 점·공백도 뺀다 — 윈도는 `이름.` 이나 `이름 ` 으로 된
 * 파일을 만들지 못한다(탐색기에서 조용히 실패한다). 책 제목이 그대로 폴더 이름이 되므로
 * 여기서 걸러야 한다.
 *
 * 쉼표·느낌표·공백은 **남긴다** — 윈도가 허용하고, 「그레그, 경주로 폴짝!」은 그 모양이라야
 * 운영자가 자기 책으로 알아본다.
 */
const BANNED = new Set(['\\', "/", ":", "*", "?", '"', "<", ">", "|"]);

export function safeFileName(s: string, fallback = "이름없음"): string {
  let out = "";
  for (const ch of s) {
    if (BANNED.has(ch)) continue;
    if (ch.codePointAt(0)! < 0x20) continue; // 제어문자
    out += ch;
  }
  out = out.replace(/[.\s]+$/u, "").trim();
  return out || fallback;
}
